import { ChangeEvent, DragEvent, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from '@reduxjs/toolkit'
import { compose } from 'redux'
import styled from 'styled-components'

import { SecondaryButton } from '../shared/styledComponents'
import type { GlobalState } from '../types'
import {
  addError,
  clearError,
  createImagePreview,
  uploadSelectCategory
} from './upload-actions'
import {
  EXCEEDED_MAX,
  EXCEEDED_MAX_QTY,
  MAX_QUANTITY,
  MAX_UPLOAD_SIZE
} from './upload-constants'
import { getFilesLength, getUploadButtonDisabledStatus, isS3Approved } from './upload-selectors'

const Container = styled.div`
 text-align: center;
`

const FileInput = styled.input`
 display: none;
`
const FileButton = styled(SecondaryButton)`
  width: 250px;
  margin: 36px auto 25px auto;

  @media (max-width: 468px) {
    width: 75%;
    margin: 36px auto;
    float: none;
  }
`

const generateRandomNumberString = (value: number) => {
  // Random number string of length value
  const array = new Uint8Array(value)
  const rand = window.crypto.getRandomValues(array).join('')
  return rand.slice(0, value)
}

const greaterThanMax = (value: number) => value > MAX_UPLOAD_SIZE
const generateId = (name: string) =>
  `${name.replace(/\s/g,'').substring(1, 10)}${generateRandomNumberString(10)}`

type FileObjType = {
  id: string
  file: File
  category: string
}

type ParsedFileObj = {
  files: FileObjType[]
  errors: string[]
}

type UploadButtonProps = {
  disabled: boolean
  onAddError(type: string, total: number | string): void
  category: string
  onCreatePreview(files: FileObjType[]): void
  onSelectCategory(category: string): void
  fileLength: number
  id?: string
}

const isDragEvent = (
  event: ChangeEvent<HTMLInputElement> | DragEvent<HTMLDivElement>
): event is DragEvent<HTMLDivElement> => {
  if ((event as DragEvent).dataTransfer) return true
  return false
}

const UploadButtonComponent = (props: UploadButtonProps) => {
  const {
    disabled,
    fileLength,
    onAddError,
    category,
    onCreatePreview,
    onSelectCategory
  } = props
  const fileInput = useRef<HTMLInputElement | null>(null)

  const validateQuantity = useCallback((fileObject: FileList | null) => {
    if (!fileObject) return new FileList()
    const total = fileLength + fileObject.length
    if (total > MAX_QUANTITY) {
      onAddError(EXCEEDED_MAX_QTY, total)
      return new FileList()
    }
    return fileObject
  }, [onAddError, fileLength])

  const parseFileObject = useCallback(
    (fileObject: FileList) => Array.from(fileObject).reduce<ParsedFileObj>(
      (acc, file) => {
        const id = generateId(file.name)
        if (isS3Approved(file)) {
          return {
            ...acc,
            files: [
              ...acc.files,
              {
                id,
                file,
                category
              }
            ]
          }
        } else if(greaterThanMax(file.size)) {
          return {
            ...acc,
            errors: [
              ...acc.errors,
              `${file.name}`
            ]
          }
        } else {
          return {
            ...acc,
            files: [
              ...acc.files,
              {
                id,
                file,
                category
              }
            ]
          }
        }
      },
      { files: [], errors: [] }
    ),
    [category]
  )


  const previewFiles = useCallback((fileArray: ParsedFileObj) => {
    onCreatePreview(fileArray.files)
    return fileArray
  }, [onCreatePreview])

  const fileErrors = useCallback((fileArray: ParsedFileObj) => {
    fileArray.errors.forEach(e => onAddError(EXCEEDED_MAX, e))
    return fileArray
  }, [onAddError])

  const clearCategory = useCallback((fileArray: ParsedFileObj) => {
    onSelectCategory('')
    return fileArray
  }, [onSelectCategory])

  const processFiles = compose(
    previewFiles,
    fileErrors,
    clearCategory
  )

  const setFileInputRef = useCallback((element: HTMLInputElement | null) => {
    fileInput.current = element
  }, [fileInput])

  const loadFiles = useCallback(
    (event: ChangeEvent<HTMLInputElement> | DragEvent<HTMLDivElement>) => {
      const validQuantity = isDragEvent(event)
        ? validateQuantity(event.dataTransfer.files)
        : validateQuantity(event.target.files)

      processFiles(
        parseFileObject(validQuantity)
      )
      if (!fileInput.current) return
      fileInput.current.value = ''
    },
    [validateQuantity, processFiles, parseFileObject, fileInput]
  )

  const openFileDialog = useCallback(() => {
    if (fileInput.current) {
      fileInput.current.click()
    }
  }, [fileInput])

  return (
    <Container>
      <FileButton
        disabled={disabled}
        onClick={openFileDialog}>
        Select Files
      </FileButton>
      <FileInput
        id="selectFile"
        ref={setFileInputRef}
        onChange={loadFiles}
        disabled={disabled}
        type="file"
        multiple
      />
    </Container>
  )
}

const mapStateToProps = (state: GlobalState) => ({
  fileLength: getFilesLength(state),
  disabled: getUploadButtonDisabledStatus(state),
  category: state.upload.category
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onCreatePreview: (files: FileObjType[]) => dispatch(createImagePreview(files)),
  onAddError: (name: string, error: number | string) => dispatch(addError(name, error)),
  onClearError: (name: string) => dispatch(clearError(name)),
  onSelectCategory: (category: string) => dispatch(uploadSelectCategory(category)),
})

const UploadButton = connect(mapStateToProps, mapDispatchToProps)(UploadButtonComponent)

export { UploadButton }
